/**
 * Theme Provider for Parento Design System
 * 
 * A comprehensive theme provider that integrates with the design system.
 * Supports light/dark mode, system preferences, and design token management.
 */

import React, { useState, useEffect, ReactNode } from 'react';
import { colors, darkModeColors } from '../tokens/colors';
import { ThemeContext, Theme, ThemeContextType } from './ThemeContext';

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  enableSystem?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
  storageKey = 'parento-theme',
  enableSystem = true,
}) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme;

    const storedTheme = localStorage.getItem(storageKey) as Theme;
    if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
      return storedTheme;
    }
    return defaultTheme;
  });

  const isDarkMode =
    theme === 'dark' ||
    (theme === 'system' &&
      enableSystem &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  useEffect(() => {
    const root = window.document.documentElement;
    const body = window.document.body;

    if (!root || !body) return;

    root.classList.remove('light', 'dark');
    body.classList.remove('light', 'dark');

    if (isDarkMode) {
      root.classList.add('dark');
      body.classList.add('dark');
    } else {
      root.classList.add('light');
      body.classList.add('light');
    }

    const currentColors = isDarkMode ? { ...colors, ...darkModeColors } : colors;

    Object.entries(currentColors).forEach(([category, colorSet]) => {
      if (typeof colorSet === 'object' && colorSet !== null) {
        Object.entries(colorSet).forEach(([key, value]) => {
          if (typeof value === 'string') {
            root.style.setProperty(`--color-${category}-${key}`, value);
          }
        });
      }
    });

    localStorage.setItem(storageKey, theme);
  }, [theme, isDarkMode, storageKey]);

  useEffect(() => {
    if (theme === 'system' && enableSystem) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        const root = window.document.documentElement;

        if (!root) return;

        root.classList.remove('light', 'dark');

        if (e.matches) {
          root.classList.add('dark');
        } else {
          root.classList.add('light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, enableSystem]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(prevTheme => {
      if (prevTheme === 'dark' || (prevTheme === 'system' && isDarkMode)) {
        return 'light';
      }
      return 'dark';
    });
  };

  const contextValue: ThemeContextType = {
    theme,
    isDarkMode,
    setTheme,
    toggleTheme,
    colors: isDarkMode ? { ...colors, ...darkModeColors } : colors,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

